import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { request, restoreSession, tokenStore } from './client'
import { closeLiveChannel } from './live'

/**
 * Shared authentication for all three apps.
 *
 * The access token lives in memory only and the refresh token in an httpOnly
 * cookie the page cannot read — an XSS that can read a refresh token is a
 * permanent account takeover, so it must never be reachable from JavaScript.
 * A reload restores the session by exchanging the cookie.
 */
export const ROLES = [
  'CLIENT',
  'PROVIDER',
  'DRIVER',
  'SUPER_ADMIN',
  'OPS',
  'KYC_REVIEWER',
  'FINANCE',
  'SUPPORT',
  'MARKETING',
  'STATE',
] as const
export type Role = (typeof ROLES)[number]

export type CurrentUser = {
  id: string
  firstName: string
  lastName: string
  email: string
  /** The number this account signs in with; the e-mail here is optional. */
  phone: string | null
  birthDate: string | null
  /**
   * The object key of the profile photo, not a URL: a download URL is presigned
   * and expires, so it is exchanged when something actually renders the image.
   */
  photoKey: string | null
  roles: Role[]
  avatarUrl?: string
}

export type AuthState =
  { status: 'loading' } | { status: 'authenticated'; user: CurrentUser } | { status: 'anonymous' }

type AuthContextValue = {
  state: AuthState
  login: (email: string, password: string) => Promise<void>
  /**
   * Phone + password — the ordinary way into AC and AF.
   *
   * Throws `ApiError`; the two the caller must branch on are
   * `password-not-set` (send them to the code path) and `account-locked`
   * (three wrong guesses; send them to the unlock path).
   */
  loginWithPassword: (phone: string, password: string) => Promise<void>
  loginWithOtp: (phone: string, code: string) => Promise<void>
  requestOtp: (phone: string) => Promise<void>
  /** "Mot de passe oublié" on a phone account: a code, not an e-mail link. */
  requestPasswordResetCode: (phone: string) => Promise<void>
  /** Spends that code, sets the password, and signs in. */
  resetPasswordWithCode: (phone: string, code: string, password: string) => Promise<void>
  /** Sends the code that lifts the lock three wrong passwords put on an account. */
  requestUnlockCode: (phone: string) => Promise<void>
  /** Spends it, lifts the lock, and signs in. */
  unlockWithCode: (phone: string, code: string) => Promise<void>
  /**
   * Google Identity Services credential in, either a session or Google's
   * profile back out. `accountFound: false` means no account matched by
   * Google subject or e-mail — phone stays this platform's identity, so the
   * caller carries the profile into the ordinary phone signup instead.
   */
  loginWithGoogle: (idToken: string) => Promise<GoogleSignInResult>
  register: (form: Registration) => Promise<void>
  completeRegistration: (phone: string, code: string) => Promise<void>
  logout: () => Promise<void>
  hasRole: (...roles: Role[]) => boolean
  /** Re-reads `/auth/me` after a profile edit, so a name or e-mail change shows up immediately. */
  refreshUser: () => Promise<void>
}

export type GoogleSignInResult = {
  accountFound: boolean
  email: string
  firstName: string
  lastName: string
  /** Google's own profile photo, if it has one — null otherwise. */
  pictureUrl: string | null
}

/** What the three signup steps collect (AC-002). */
export type Registration = {
  accountType: 'INDIVIDUAL' | 'BUSINESS'
  phone: string
  /**
   * Chosen during signup and required from here on.
   *
   * Signup is the one moment a code is unavoidable — the number has to be
   * proven — so it is also the moment to collect the credential that stops
   * every later sign-in costing an SMS.
   */
  password: string
  email?: string | undefined
  firstName?: string | undefined
  lastName?: string | undefined
  /** ISO date; the API rejects a future one. */
  birthDate?: string | undefined
  companyName?: string | undefined
  /** Unset or empty is a client (AC); one or more activities is a Waago Pro provider signup (AF). */
  activities?: string[] | undefined
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  // All three apps already mount this inside their own <QueryClientProvider>
  // (see each app's main.tsx), so reaching for it here is safe rather than
  // threading a callback down through router.tsx's <Root>.
  const queryClient = useQueryClient()
  const [state, setState] = useState<AuthState>({ status: 'loading' })

  const loadSession = useCallback(async () => {
    // Single-flight, so a double-mount or two components restoring at once
    // rotate the refresh token once between them rather than twice.
    if (!(await restoreSession())) throw new Error('no session')
    const user = await request<CurrentUser>('/auth/me')
    setState({ status: 'authenticated', user })
  }, [])

  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        await loadSession()
      } catch {
        if (!cancelled) setState({ status: 'anonymous' })
      }
    })()
    return () => {
      cancelled = true
    }
  }, [loadSession])

  const login = useCallback(async (email: string, password: string) => {
    const { accessToken } = await request<{ accessToken: string }>('/auth/login', {
      method: 'POST',
      body: { email, password },
      auth: false,
    })
    tokenStore.set(accessToken)
    const user = await request<CurrentUser>('/auth/me')
    setState({ status: 'authenticated', user })
  }, [])

  /** Exchanges an access token for a session, however it was earned. */
  const adopt = useCallback(async (accessToken: string) => {
    tokenStore.set(accessToken)
    const user = await request<CurrentUser>('/auth/me')
    setState({ status: 'authenticated', user })
  }, [])

  const loginWithOtp = useCallback(
    async (phone: string, code: string) => {
      const { accessToken } = await request<{ accessToken: string }>('/auth/otp/verify', {
        method: 'POST',
        body: { phone, code },
        auth: false,
      })
      await adopt(accessToken)
    },
    [adopt],
  )

  const loginWithGoogle = useCallback(
    async (idToken: string) => {
      const res = await request<{
        accessToken: string | null
        accountFound: boolean
        email: string
        firstName: string
        lastName: string
        pictureUrl: string | null
      }>('/auth/google', {
        method: 'POST',
        body: { idToken },
        auth: false,
      })
      if (res.accountFound && res.accessToken) {
        await adopt(res.accessToken)
      }
      return {
        accountFound: res.accountFound,
        email: res.email,
        firstName: res.firstName,
        lastName: res.lastName,
        pictureUrl: res.pictureUrl,
      }
    },
    [adopt],
  )

  const requestOtp = useCallback(async (phone: string) => {
    await request<void>('/auth/otp/request', {
      method: 'POST',
      body: { phone },
      auth: false,
    })
  }, [])

  /**
   * Phone + password, no SMS.
   *
   * This is what replaced a code on every connection — the operator was being
   * billed for one message per sign-in. A code is now spent only at signup, on
   * a forgotten password, and to lift a lock.
   */
  const loginWithPassword = useCallback(
    async (phone: string, password: string) => {
      const { accessToken } = await request<{ accessToken: string }>('/auth/login/phone', {
        method: 'POST',
        body: { phone, password },
        auth: false,
      })
      await adopt(accessToken)
    },
    [adopt],
  )

  const requestPasswordResetCode = useCallback(async (phone: string) => {
    await request<void>('/auth/password-reset/phone/request', {
      method: 'POST',
      body: { phone },
      auth: false,
    })
  }, [])

  const resetPasswordWithCode = useCallback(
    async (phone: string, code: string, password: string) => {
      const { accessToken } = await request<{ accessToken: string }>(
        '/auth/password-reset/phone/confirm',
        { method: 'POST', body: { phone, code, password }, auth: false },
      )
      await adopt(accessToken)
    },
    [adopt],
  )

  const requestUnlockCode = useCallback(async (phone: string) => {
    await request<void>('/auth/unlock/request', {
      method: 'POST',
      body: { phone },
      auth: false,
    })
  }, [])

  const unlockWithCode = useCallback(
    async (phone: string, code: string) => {
      const { accessToken } = await request<{ accessToken: string }>('/auth/unlock/verify', {
        method: 'POST',
        body: { phone, code },
        auth: false,
      })
      await adopt(accessToken)
    },
    [adopt],
  )

  /**
   * Steps 1 and 2. Creates a pending account and sends the verification code.
   * The account cannot sign in until `completeRegistration` succeeds.
   */
  const register = useCallback(async (form: Registration) => {
    await request<void>('/auth/register', { method: 'POST', body: form, auth: false })
  }, [])

  /** Step 3: proves the number, activates the account, and signs in. */
  const completeRegistration = useCallback(
    async (phone: string, code: string) => {
      const { accessToken } = await request<{ accessToken: string }>('/auth/register/verify', {
        method: 'POST',
        body: { phone, code },
        auth: false,
      })
      await adopt(accessToken)
    },
    [adopt],
  )

  const logout = useCallback(async () => {
    try {
      await request<void>('/auth/logout', { method: 'POST' })
    } finally {
      tokenStore.set(null)
      // A signed-out session must leave nothing open. The socket outlives any
      // one screen by design, so nothing else would ever close it.
      closeLiveChannel()
      setState({ status: 'anonymous' })
      // Otherwise a second account signing in on the same device can briefly
      // render the first account's cached profile, photo, or e-mail before
      // its own data has arrived — query keys here aren't scoped by account.
      queryClient.clear()
    }
  }, [queryClient])

  const refreshUser = useCallback(async () => {
    const user = await request<CurrentUser>('/auth/me')
    setState({ status: 'authenticated', user })
  }, [])

  const hasRole = useCallback(
    (...roles: Role[]) =>
      state.status === 'authenticated' &&
      (state.user.roles.includes('SUPER_ADMIN') || roles.some((r) => state.user.roles.includes(r))),
    [state],
  )

  const value = useMemo(
    () => ({
      state,
      login,
      loginWithPassword,
      loginWithOtp,
      requestOtp,
      requestPasswordResetCode,
      resetPasswordWithCode,
      requestUnlockCode,
      unlockWithCode,
      loginWithGoogle,
      register,
      completeRegistration,
      logout,
      hasRole,
      refreshUser,
    }),
    [
      state,
      login,
      loginWithPassword,
      loginWithOtp,
      requestOtp,
      requestPasswordResetCode,
      resetPasswordWithCode,
      requestUnlockCode,
      unlockWithCode,
      loginWithGoogle,
      register,
      completeRegistration,
      logout,
      hasRole,
      refreshUser,
    ],
  )
  return <AuthContext value={value}>{children}</AuthContext>
}

export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}
